import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Filter } from "lucide-react";
import { Header, Footer, Loading } from "../components/Shell";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";

// Sprint 3: Materi — knowledge library / search page (bukan pengganti Learning Path)
export function Materi() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");

  useSeo({
    title: "Materi — Cari Materi Digital Marketing",
    description: "Cari materi Shopee Ads, TikTok Ads, Meta Ads, dan lainnya di MASTOFA.COM.",
    path: "/materi",
  });

  useEffect(() => {
    api("/courses").then((docs) => {
      setCourses(docs);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let items = courses;
    if (q.trim()) {
      const n = q.toLowerCase();
      items = items.filter(
        (c) =>
          (c.title || "").toLowerCase().includes(n) ||
          (c.description || "").toLowerCase().includes(n) ||
          (c.category || "").toLowerCase().includes(n) ||
          (c.learning_category || "").toLowerCase().includes(n)
      );
    }
    if (levelFilter !== "") {
      items = items.filter((c) => String(c.path_level) === String(levelFilter));
    }
    if (catFilter) {
      items = items.filter((c) => c.learning_category === catFilter);
    }
    return items;
  }, [courses, q, levelFilter, catFilter]);

  const allCats = useMemo(() => {
    const set = new Set();
    courses.forEach((c) => c.learning_category && set.add(c.learning_category));
    return Array.from(set);
  }, [courses]);

  if (loading) return <Loading />;

  return (
    <div>
      <Header />
      <main style={{ padding: "40px 20px", background: "#f8fafc", minHeight: "60vh" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <span className="section-kicker">MATERI</span>
            <h1 style={{ fontSize: "2.2rem", marginTop: 12 }}>
              Cari <span>materi</span> yang Anda butuhkan.
            </h1>
            <p style={{ color: "#64748b", maxWidth: 520, margin: "12px auto 0" }}>
              Knowledge library. Cari cepat berdasarkan topik, level, atau kategori.
              Untuk belajar runtut dari awal, buka <Link to="/belajar" style={{ color: "#2563eb", textDecoration: "underline" }}>Belajar</Link>.
            </p>
          </div>

          {/* Search + Filter Bar */}
          <div style={{
            background: "#fff", padding: 20, borderRadius: 12,
            border: "1px solid #e2e8f0", marginBottom: 24,
            display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12,
          }} data-testid="materi-filter-bar">
            <div style={{ position: "relative" }}>
              <Search size={16} style={{ position: "absolute", left: 12, top: 12, color: "#94a3b8" }} />
              <input
                type="text"
                placeholder="Cari: Shopee, GMV Max, Meta, CPAS..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="materi-search"
                style={{
                  width: "100%", padding: "10px 12px 10px 36px",
                  border: "1px solid #e2e8f0", borderRadius: 8,
                }}
              />
            </div>
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              data-testid="materi-level-filter"
              style={{ padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8 }}
            >
              <option value="">Semua Level</option>
              <option value="0">Level 0 · Fundamental</option>
              <option value="1">Level 1 · Marketplace</option>
              <option value="2">Level 2 · Social Ads</option>
              <option value="3">Level 3 · Advanced</option>
              <option value="4">Level 4 · Scaling</option>
            </select>
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              data-testid="materi-cat-filter"
              style={{ padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8 }}
            >
              <option value="">Semua Kategori</option>
              {allCats.map((c) => {
                const label = c === "cpas" ? "CPAS"
                  : c === "meta_service_ads" ? "Meta Ads untuk Jasa"
                  : c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
                return <option key={c} value={c}>{label}</option>;
              })}
            </select>
          </div>

          {/* Result count */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: 16, color: "#64748b", fontSize: "0.9rem",
          }}>
            <span data-testid="materi-count">
              <b>{filtered.length}</b> materi ditemukan
              {q && ` untuk "${q}"`}
            </span>
            {(q || levelFilter || catFilter) && (
              <button
                onClick={() => { setQ(""); setLevelFilter(""); setCatFilter(""); }}
                style={{
                  background: "transparent", border: "none",
                  color: "#2563eb", cursor: "pointer", fontSize: "0.85rem",
                }}
                data-testid="materi-clear-filters"
              >
                Reset filter
              </button>
            )}
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div style={{
              background: "#fff", padding: 60, borderRadius: 12,
              textAlign: "center", border: "1px solid #e2e8f0",
            }} data-testid="materi-empty">
              <Filter size={40} style={{ color: "#cbd5e1", margin: "0 auto 12px" }} />
              <b style={{ display: "block", marginBottom: 8 }}>Tidak ada materi cocok</b>
              <small style={{ color: "#64748b" }}>Coba kata kunci berbeda atau reset filter.</small>
            </div>
          ) : (
            <div style={{
              display: "grid", gap: 16,
              gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
            }} data-testid="materi-grid">
              {filtered.map((c) => (
                <Link
                  key={c.slug}
                  to={`/courses/${c.slug}`}
                  data-testid={`materi-item-${c.slug}`}
                  style={{
                    background: "#fff", padding: 20, borderRadius: 12,
                    border: "1px solid #e2e8f0", textDecoration: "none",
                    color: "#0f172a", display: "flex", flexDirection: "column", gap: 12,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2563eb"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {c.path_level != null && (
                      <span style={{ background: "#eff6ff", color: "#2563eb", padding: "2px 8px", borderRadius: 6, fontSize: "0.7rem", fontWeight: 600 }}>
                        Level {c.path_level}
                      </span>
                    )}
                    {c.level && (
                      <span style={{ background: "#f1f5f9", color: "#475569", padding: "2px 8px", borderRadius: 6, fontSize: "0.7rem" }}>
                        {c.level}
                      </span>
                    )}
                  </div>
                  <b style={{ fontSize: "1rem", lineHeight: 1.3 }}>{c.title}</b>
                  {c.description && (
                    <small style={{ color: "#64748b", lineHeight: 1.4 }}>
                      {c.description.slice(0, 90)}{c.description.length > 90 ? "..." : ""}
                    </small>
                  )}
                  <div style={{
                    marginTop: "auto", paddingTop: 12, borderTop: "1px solid #f1f5f9",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <b style={{ color: "#2563eb" }}>Rp {Number(c.price).toLocaleString("id-ID")}</b>
                    <span style={{ color: "#2563eb", fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      Buka <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
